import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { PropertyCategorySchema } from '../common/property-taxonomy.schema';
import { FactKeyGroupSchema } from './admin-fact-keys.schemas';

// The four products the card checks — equal to FACT_KEY_PRODUCTS; VALUATION_AGENT_REQUEST_KIND_VALUES is a
// subset (the card matches agent outcomes by kind, EXPOSE never matches one). Both pinned by a contract test.
export const COMPLETENESS_PRODUCT_VALUES = ['ACQUISITION_BRIEFING', 'VALUATION_1A', 'VALUATION_1B', 'EXPOSE'] as const;
export const CompletenessProductSchema = z.enum(COMPLETENESS_PRODUCT_VALUES);
export type CompletenessProduct = z.infer<typeof CompletenessProductSchema>;

export const COMPLETENESS_STATUS_VALUES = ['COMPLETE', 'INCOMPLETE'] as const;
export const CompletenessStatusSchema = z.enum(COMPLETENESS_STATUS_VALUES);
export type CompletenessStatus = z.infer<typeof CompletenessStatusSchema>;

// CONTRADICTED / ASSUMED mark a present field, UNKNOWN a missing one.
export const COMPLETENESS_MARKER_VALUES = ['CONTRADICTED', 'ASSUMED', 'UNKNOWN'] as const;
export const CompletenessMarkerSchema = z.enum(COMPLETENESS_MARKER_VALUES);
export type CompletenessMarker = z.infer<typeof CompletenessMarkerSchema>;

export const CompletenessFieldSchema = z.object({
  catalogId: z.string(), // the fact-key ID, e.g. 'FLA.WOHNFLAECHE', 'LAG.ADRESSE.plz'
  label: z.string(), // the catalog entry's Bezeichnung, operator-maintained display copy
  groupId: FactKeyGroupSchema,
  missing: z.boolean(),
  markers: z.array(CompletenessMarkerSchema), // ordered CONTRADICTED, ASSUMED, UNKNOWN
});
export type CompletenessField = z.infer<typeof CompletenessFieldSchema>;

export const ProductCompletenessSchema = z.object({
  product: CompletenessProductSchema,
  status: CompletenessStatusSchema, // COMPLETE iff missingRequiredCount === 0
  missingRequiredCount: z.number().int().nonnegative(),
  // Every missing required field, plus every present required field carrying a marker, in card order.
  required: z.array(CompletenessFieldSchema),
  // Every missing recommended field, in card order. Never affects `status`.
  missingRecommended: z.array(CompletenessFieldSchema),
});
export type ProductCompleteness = z.infer<typeof ProductCompletenessSchema>;

export const PropertyCompletenessResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  category: PropertyCategorySchema,
  // false → `products` is [] and the card shows the single „no needs“ sentence.
  hasNeeds: z.boolean(),
  // Exactly the four products in COMPLETENESS_PRODUCT_VALUES order when hasNeeds; [] otherwise.
  products: z.array(ProductCompletenessSchema),
});
export type PropertyCompletenessResponse = z.infer<typeof PropertyCompletenessResponseSchema>;
